/**
 * Social Media Tracker — Standalone Zustand-Store + Firestore `socialMediaLogs`.
 * Scheibe 1: Types, CRUD, Stats. Kein UI.
 */

import { create } from 'zustand';
import {
    collection,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    Unsubscribe,
    where,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import {
    SocialMediaLogType,
    SocialMediaMonthlyStats,
    clampVacationDays,
    computeMonthlyStats,
    isSocialMediaLogType,
    monthKeyFromDate,
    pointsFor,
    todayIsoLocal,
} from '../utils/socialMediaScoring';

export type SocialMediaLog = {
    id: string;
    userId: string;
    date: string; // YYYY-MM-DD
    month: string; // YYYY-MM
    type: SocialMediaLogType;
    points: number;
    count: number;
    createdAt: unknown; // Firestore Timestamp | serverTimestamp sentinel
    /** Snapshot beim Schreiben; Stats können Exempt zusätzlich per Opts überschreiben */
    isExemptFromMalus: boolean;
    /** Snapshot für Leaderboard-Namensauflösung */
    userEmail?: string;
    displayName?: string;
};

export type SocialMediaMonthMeta = {
    id: string;
    userId: string;
    month: string;
    vacationDays: number;
    userEmail?: string;
};

type AddLogOpts = {
    isExemptFromMalus?: boolean;
    userEmail?: string;
    displayName?: string;
};

type StatsOpts = {
    isExemptFromMalus?: boolean;
    vacationDays?: number;
    email?: string | null;
    /** Wenn true: erst neu aus Firestore laden, dann rechnen */
    refresh?: boolean;
};

interface SocialMediaStore {
    logs: SocialMediaLog[];
    /** Alle Logs eines Monats (für Leaderboard), getrennt von user-scoped `logs` */
    monthAllLogs: SocialMediaLog[];
    /** vacationDays je Monat → userId (M-1-Fetch darf aktuellen Monat nicht überschreiben) */
    monthMetaByMonth: Record<string, Record<string, SocialMediaMonthMeta>>;
    selectedMonth: string;
    selectedUserId: string | null;
    isLoading: boolean;
    error: string | null;

    addLog: (
        userId: string,
        type: SocialMediaLogType,
        count: number,
        date?: string,
        opts?: AddLogOpts
    ) => Promise<SocialMediaLog | null>;

    deleteLog: (logId: string) => Promise<boolean>;

    fetchMonthlyLogs: (userId: string, month: string) => Promise<SocialMediaLog[]>;

    /** Alle Logs eines Monats (alle User) — Leaderboard */
    fetchMonthAllLogs: (month: string) => Promise<SocialMediaLog[]>;

    /** Live: user-scoped Logs (Unsubscribe bei Monatswechsel/Unmount) */
    subscribeMonthlyLogs: (userId: string, month: string) => void;
    unsubscribeMonthlyLogs: () => void;

    /** Live: alle Logs eines Monats */
    subscribeMonthAllLogs: (month: string) => void;
    unsubscribeMonthAllLogs: () => void;

    fetchMonthMeta: (month: string) => Promise<Record<string, SocialMediaMonthMeta>>;

    setVacationDays: (
        userId: string,
        month: string,
        vacationDays: number,
        opts?: { userEmail?: string }
    ) => Promise<boolean>;

    getMonthlyStats: (
        userId: string,
        month: string,
        opts?: StatsOpts
    ) => Promise<SocialMediaMonthlyStats>;

    /** Global: monetärer Malus-Abzug in Abrechnung (Testphasen-Schalter) */
    isDeductionActive: boolean;
    settingsLoaded: boolean;
    ensureSettingsLoaded: () => void;
    subscribeSettings: () => void;
    unsubscribeSettings: () => void;
    setDeductionActive: (active: boolean) => Promise<boolean>;
    toggleDeductionActive: () => Promise<boolean>;
}

const COLLECTION = 'socialMediaLogs';
const META_COLLECTION = 'socialMediaMonthMeta';
const SETTINGS_COLLECTION = 'socialMediaSettings';
const SETTINGS_DOC_ID = 'global';

const metaDocId = (userId: string, month: string) => `${userId}_${month}`;

/** Modul-Level Listener — sauberes Unsubscribe bei Monatswechsel */
let monthlyLogsUnsub: Unsubscribe | null = null;
let monthAllLogsUnsub: Unsubscribe | null = null;
let settingsUnsub: Unsubscribe | null = null;
let settingsRefCount = 0;

/** Defense-in-Depth: Schreibzugriff nur mit Auth-UID === userId */
function assertOwnAuthUid(userId: string): string | null {
    const authUid = String(auth.currentUser?.uid || '').trim();
    const uid = String(userId || '').trim();
    if (!authUid) {
        return 'Nicht angemeldet — Schreibzugriff verweigert.';
    }
    if (!uid || authUid !== uid) {
        return 'userId stimmt nicht mit dem angemeldeten Konto überein.';
    }
    return null;
}

const normalizeDateIso = (date?: string): string => {
    const raw = String(date || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (raw) {
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        }
    }
    return todayIsoLocal();
};

const mapDocToLog = (id: string, data: Record<string, unknown>): SocialMediaLog | null => {
    const type = data.type;
    if (!isSocialMediaLogType(type)) return null;
    const countRaw = Number(data.count);
    const count = Number.isFinite(countRaw) && countRaw > 0 ? Math.floor(countRaw) : 1;
    const date = String(data.date || '').trim();
    const month = String(data.month || monthKeyFromDate(date) || '').trim();
    const pointsRaw = Number(data.points);
    const points =
        Number.isFinite(pointsRaw) && pointsRaw >= 0 ? pointsRaw : pointsFor(type, count);

    const userEmail = String(data.userEmail || '').trim().toLowerCase() || undefined;
    const displayName = String(data.displayName || '').trim() || undefined;

    return {
        id,
        userId: String(data.userId || ''),
        date,
        month,
        type,
        points,
        count,
        createdAt: data.createdAt ?? null,
        isExemptFromMalus: data.isExemptFromMalus === true,
        userEmail,
        displayName,
    };
};

const snapToLogs = (snap: {
    forEach: (cb: (d: { id: string; data: () => any }) => void) => void;
}): SocialMediaLog[] => {
    const logs: SocialMediaLog[] = [];
    snap.forEach((d) => {
        const mapped = mapDocToLog(d.id, d.data() as Record<string, unknown>);
        if (mapped) logs.push(mapped);
    });
    logs.sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return logs;
};

const useSocialMediaStore = create<SocialMediaStore>((set, get) => ({
    logs: [],
    monthAllLogs: [],
    monthMetaByMonth: {},
    selectedMonth: '',
    selectedUserId: null,
    isLoading: false,
    error: null,
    isDeductionActive: true,
    settingsLoaded: false,

    addLog: async (userId, type, count, date, opts) => {
        const uid = String(userId || '').trim();
        const authErr = assertOwnAuthUid(uid);
        if (authErr) {
            set({ error: authErr });
            return null;
        }
        if (!uid) {
            set({ error: 'userId fehlt.' });
            return null;
        }
        if (!isSocialMediaLogType(type)) {
            set({ error: 'Ungültiger Log-Typ.' });
            return null;
        }

        const safeCount =
            Number.isFinite(count) && count > 0 ? Math.floor(count) : 1;
        const dateIso = normalizeDateIso(date);
        const month = monthKeyFromDate(dateIso);
        if (!month) {
            set({ error: 'Ungültiges Datum.' });
            return null;
        }

        const points = pointsFor(type, safeCount);
        const isExemptFromMalus = opts?.isExemptFromMalus === true;
        const userEmail = String(opts?.userEmail || '').trim().toLowerCase() || undefined;
        const displayName = String(opts?.displayName || '').trim() || undefined;

        set({ isLoading: true, error: null });
        try {
            const newRef = doc(collection(db, COLLECTION));
            const payload: Omit<SocialMediaLog, 'id'> & { id: string } = {
                id: newRef.id,
                userId: uid,
                date: dateIso,
                month,
                type,
                points,
                count: safeCount,
                createdAt: serverTimestamp(),
                isExemptFromMalus,
                ...(userEmail ? { userEmail } : {}),
                ...(displayName ? { displayName } : {}),
            };

            await setDoc(newRef, payload);

            // Optimistic local merge; onSnapshot überschreibt gleich sauber
            const localLog: SocialMediaLog = {
                ...payload,
                createdAt: new Date().toISOString(),
            };

            const { selectedUserId, selectedMonth, logs, monthAllLogs } = get();
            const nextLogs =
                selectedUserId === uid && selectedMonth === month
                    ? [...logs.filter((l) => l.id !== localLog.id), localLog].sort((a, b) =>
                          String(b.date).localeCompare(String(a.date))
                      )
                    : logs;
            const nextMonthAll =
                selectedMonth === month || monthAllLogs.some((l) => l.month === month)
                    ? [
                          ...monthAllLogs.filter(
                              (l) => !(l.month === month && l.id === localLog.id)
                          ),
                          localLog,
                      ]
                    : monthAllLogs;

            set({
                logs: nextLogs,
                monthAllLogs: nextMonthAll,
                isLoading: false,
            });

            return localLog;
        } catch (err: any) {
            console.error('[socialMediaStore] addLog fehlgeschlagen:', err);
            set({
                error: err?.message || 'Fehler beim Speichern des Social-Media-Logs.',
                isLoading: false,
            });
            return null;
        }
    },

    deleteLog: async (logId) => {
        const id = String(logId || '').trim();
        if (!id) {
            set({ error: 'logId fehlt.' });
            return false;
        }
        const authUid = String(auth.currentUser?.uid || '').trim();
        if (!authUid) {
            set({ error: 'Nicht angemeldet — Löschen verweigert.' });
            return false;
        }

        set({ isLoading: true, error: null });
        try {
            const existing = get().logs.find((l) => l.id === id)
                || get().monthAllLogs.find((l) => l.id === id);
            if (existing && existing.userId && existing.userId !== authUid) {
                set({
                    error: 'Nur eigene Einträge können gelöscht werden.',
                    isLoading: false,
                });
                return false;
            }
            await deleteDoc(doc(db, COLLECTION, id));
            set({
                logs: get().logs.filter((l) => l.id !== id),
                monthAllLogs: get().monthAllLogs.filter((l) => l.id !== id),
                isLoading: false,
            });
            return true;
        } catch (err: any) {
            console.error('[socialMediaStore] deleteLog fehlgeschlagen:', err);
            set({
                error: err?.message || 'Fehler beim Löschen des Social-Media-Logs.',
                isLoading: false,
            });
            return false;
        }
    },

    fetchMonthlyLogs: async (userId, month) => {
        const uid = String(userId || '').trim();
        const monthKey = String(month || '').trim();
        if (!uid || !/^\d{4}-\d{2}$/.test(monthKey)) {
            set({ error: 'userId oder month ungültig.', logs: [] });
            return [];
        }

        set({ isLoading: true, error: null, selectedUserId: uid, selectedMonth: monthKey });
        try {
            const q = query(
                collection(db, COLLECTION),
                where('userId', '==', uid),
                where('month', '==', monthKey)
            );
            const snap = await getDocs(q);
            const logs = snapToLogs(snap);
            set({ logs, isLoading: false });
            return logs;
        } catch (err: any) {
            console.error('[socialMediaStore] fetchMonthlyLogs fehlgeschlagen:', err);
            set({
                error:
                    err?.message ||
                    'Fehler beim Laden. Evtl. fehlt der Firestore Composite-Index (userId + month).',
                logs: [],
                isLoading: false,
            });
            return [];
        }
    },

    fetchMonthAllLogs: async (month) => {
        const monthKey = String(month || '').trim();
        if (!/^\d{4}-\d{2}$/.test(monthKey)) {
            set({ error: 'month ungültig.', monthAllLogs: [] });
            return [];
        }

        try {
            const q = query(collection(db, COLLECTION), where('month', '==', monthKey));
            const snap = await getDocs(q);
            const logs = snapToLogs(snap);
            set({ monthAllLogs: logs, selectedMonth: monthKey, error: null });
            return logs;
        } catch (err: any) {
            console.error('[socialMediaStore] fetchMonthAllLogs fehlgeschlagen:', err);
            set({
                error:
                    err?.message ||
                    'Leaderboard-Laden fehlgeschlagen. Evtl. fehlt Index auf month.',
                monthAllLogs: [],
            });
            return [];
        }
    },

    unsubscribeMonthlyLogs: () => {
        if (monthlyLogsUnsub) {
            monthlyLogsUnsub();
            monthlyLogsUnsub = null;
        }
    },

    unsubscribeMonthAllLogs: () => {
        if (monthAllLogsUnsub) {
            monthAllLogsUnsub();
            monthAllLogsUnsub = null;
        }
    },

    subscribeMonthlyLogs: (userId, month) => {
        const uid = String(userId || '').trim();
        const monthKey = String(month || '').trim();
        get().unsubscribeMonthlyLogs();
        if (!uid || !/^\d{4}-\d{2}$/.test(monthKey)) {
            set({ error: 'userId oder month ungültig.', logs: [] });
            return;
        }

        set({ isLoading: true, error: null, selectedUserId: uid, selectedMonth: monthKey });
        const q = query(
            collection(db, COLLECTION),
            where('userId', '==', uid),
            where('month', '==', monthKey)
        );
        monthlyLogsUnsub = onSnapshot(
            q,
            (snap) => {
                set({ logs: snapToLogs(snap), isLoading: false, error: null });
            },
            (err) => {
                console.error('[socialMediaStore] subscribeMonthlyLogs:', err);
                set({
                    error:
                        err?.message ||
                        'Live-Sync fehlgeschlagen. Evtl. fehlt der Composite-Index (userId + month).',
                    logs: [],
                    isLoading: false,
                });
            }
        );
    },

    subscribeMonthAllLogs: (month) => {
        const monthKey = String(month || '').trim();
        get().unsubscribeMonthAllLogs();
        if (!/^\d{4}-\d{2}$/.test(monthKey)) {
            set({ error: 'month ungültig.', monthAllLogs: [] });
            return;
        }

        set({ selectedMonth: monthKey });
        const q = query(collection(db, COLLECTION), where('month', '==', monthKey));
        monthAllLogsUnsub = onSnapshot(
            q,
            (snap) => {
                set({ monthAllLogs: snapToLogs(snap), error: null });
            },
            (err) => {
                console.error('[socialMediaStore] subscribeMonthAllLogs:', err);
                set({
                    error:
                        err?.message ||
                        'Leaderboard Live-Sync fehlgeschlagen. Evtl. fehlt Index auf month.',
                    monthAllLogs: [],
                });
            }
        );
    },

    fetchMonthMeta: async (month) => {
        const monthKey = String(month || '').trim();
        if (!/^\d{4}-\d{2}$/.test(monthKey)) {
            return {};
        }
        try {
            const q = query(
                collection(db, META_COLLECTION),
                where('month', '==', monthKey)
            );
            const snap = await getDocs(q);
            const map: Record<string, SocialMediaMonthMeta> = {};
            snap.forEach((d) => {
                const data = d.data() as Record<string, unknown>;
                const userId = String(data.userId || '').trim();
                if (!userId) return;
                map[userId] = {
                    id: d.id,
                    userId,
                    month: monthKey,
                    vacationDays: clampVacationDays(data.vacationDays),
                    userEmail: String(data.userEmail || '').trim().toLowerCase() || undefined,
                };
            });
            // Nur diesen Monat mergen — andere Monate (z. B. aktuell vs. M-1) bleiben erhalten
            const prev = get().monthMetaByMonth || {};
            set({
                monthMetaByMonth: {
                    ...prev,
                    [monthKey]: map,
                },
                error: null,
            });
            return map;
        } catch (err: any) {
            console.error('[socialMediaStore] fetchMonthMeta fehlgeschlagen:', err);
            set({
                error: err?.message || 'Urlaubs-Meta laden fehlgeschlagen.',
            });
            return {};
        }
    },

    setVacationDays: async (userId, month, vacationDays, opts) => {
        const uid = String(userId || '').trim();
        const monthKey = String(month || '').trim();
        const authErr = assertOwnAuthUid(uid);
        if (authErr) {
            set({ error: authErr });
            return false;
        }
        if (!uid || !/^\d{4}-\d{2}$/.test(monthKey)) {
            set({ error: 'userId oder month ungültig.' });
            return false;
        }
        const days = clampVacationDays(vacationDays);
        const userEmail = String(opts?.userEmail || '').trim().toLowerCase() || undefined;
        const id = metaDocId(uid, monthKey);
        try {
            const payload = {
                id,
                userId: uid,
                month: monthKey,
                vacationDays: days,
                ...(userEmail ? { userEmail } : {}),
                updatedAt: serverTimestamp(),
            };
            await setDoc(doc(db, META_COLLECTION, id), payload, { merge: true });
            const prevAll = get().monthMetaByMonth || {};
            const prevMonth = prevAll[monthKey] || {};
            set({
                monthMetaByMonth: {
                    ...prevAll,
                    [monthKey]: {
                        ...prevMonth,
                        [uid]: {
                            id,
                            userId: uid,
                            month: monthKey,
                            vacationDays: days,
                            userEmail,
                        },
                    },
                },
                error: null,
            });
            return true;
        } catch (err: any) {
            console.error('[socialMediaStore] setVacationDays fehlgeschlagen:', err);
            set({ error: err?.message || 'Urlaubstage speichern fehlgeschlagen.' });
            return false;
        }
    },

    getMonthlyStats: async (userId, month, opts) => {
        const uid = String(userId || '').trim();
        const monthKey = String(month || '').trim();

        let logs = get().logs;
        const needsFetch =
            opts?.refresh === true ||
            get().selectedUserId !== uid ||
            get().selectedMonth !== monthKey ||
            logs.length === 0;

        if (needsFetch) {
            logs = await get().fetchMonthlyLogs(uid, monthKey);
        } else {
            logs = logs.filter((l) => l.userId === uid && l.month === monthKey);
        }

        const meta = get().monthMetaByMonth?.[monthKey]?.[uid];
        const vacationDays =
            opts?.vacationDays !== undefined
                ? clampVacationDays(opts.vacationDays)
                : meta?.vacationDays || 0;

        const exemptFromLogs = logs.some((l) => l.isExemptFromMalus === true);
        const isExemptFromMalus =
            opts?.isExemptFromMalus === true || exemptFromLogs;

        return computeMonthlyStats(logs, {
            userId: uid,
            month: monthKey,
            vacationDays,
            email: opts?.email,
            isExemptFromMalus,
        });
    },

    ensureSettingsLoaded: () => {
        settingsRefCount += 1;
        if (get().settingsLoaded || settingsUnsub) return;
        get().subscribeSettings();
    },

    subscribeSettings: () => {
        if (settingsUnsub) {
            settingsUnsub();
            settingsUnsub = null;
        }
        const ref = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
        settingsUnsub = onSnapshot(
            ref,
            (snap) => {
                const data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
                const active = data ? data.isDeductionActive !== false : true;
                set({ isDeductionActive: active, settingsLoaded: true, error: null });
            },
            (err) => {
                console.error('[socialMediaStore] settings subscribe fehlgeschlagen:', err);
                set({
                    isDeductionActive: true,
                    settingsLoaded: true,
                    error: err?.message || 'PEPulse-Settings laden fehlgeschlagen.',
                });
            }
        );
    },

    unsubscribeSettings: () => {
        settingsRefCount = Math.max(0, settingsRefCount - 1);
        if (settingsRefCount > 0) return;
        if (settingsUnsub) {
            settingsUnsub();
            settingsUnsub = null;
        }
        set({ settingsLoaded: false });
    },

    setDeductionActive: async (active) => {
        const next = active === true;
        try {
            await setDoc(
                doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID),
                {
                    isDeductionActive: next,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            set({ isDeductionActive: next, settingsLoaded: true, error: null });
            return true;
        } catch (err: any) {
            console.error('[socialMediaStore] setDeductionActive fehlgeschlagen:', err);
            set({ error: err?.message || 'Abzug-Schalter speichern fehlgeschlagen.' });
            return false;
        }
    },

    toggleDeductionActive: async () => {
        const next = !get().isDeductionActive;
        return get().setDeductionActive(next);
    },
}));

export default useSocialMediaStore;
